import { Button, Stack } from '@mui/material'
import { MouseEvent, useCallback, useState } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  StorePurchaseOrderConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  StorePurchaseHistory,
  StorePurchaseHistorySkeleton,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  purchaseHistories: Maybe<StorePurchaseOrderConnectionFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const StorePurchaseOrdersTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('storePurchaseOrders')
  const { routerNames, routerPages } = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const options = [
    { label: t('all', 'すべて'), value: undefined },
    { label: t('purchase', '購入'), value: 'purchase' },
    { label: t('refund', '返金対応'), value: 'refund' },
  ]

  const handleFilterClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLElement>, newIndex: number) => {
      setSelectedIndex(newIndex)
      setAnchorEl(null)
    },
    []
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <BaseLayout
      margin
      header={{
        left: (
          <IconButton.Back
            edge="start"
            size="medium"
            href={routerPages.Management}
          />
        ),
        title: t('title'),
      }}
    >
      <Head title={routerNames.StorePurchaseOrders} />
      <HeaderTagCell loading={state.loading && !state.purchaseHistories} />
      {/* フィルタ機能実装後、再度表示する https://app.clickup.com/t/908127/UT-12619 */}
      {/* <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ pb: 3 }}
        >
          <Button
            variant="text"
            sx={{ width: 'fit-content', mr: -2 }}
            onClick={handleFilterClick}
          >
            <Typography variant="caption" sx={{ pr: 1 }}>
              {options[selectedIndex]?.label}
            </Typography>
            <Icons.DownArrow fontSize="xSmall" sx={{ color: 'grey.600' }} />
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {options.map((option, index) => (
              <MenuItem
                sx={{ bgcolor: 'grey.50', minWidth: 200 }}
                key={index}
                selected={index === selectedIndex}
                onClick={event => handleMenuItemClick(event, index)}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack> */}
      <ScrollFetcher
        nodes={state.purchaseHistories?.edges.map(({ node }) => (
          <StorePurchaseHistory key={node.id} purchaseHistory={node} />
        ))}
        loadingComponent={
          <Stack spacing={2} sx={{ pt: 2 }}>
            {Array.from(Array(3).keys()).map(key => (
              <StorePurchaseHistorySkeleton key={key} />
            ))}
          </Stack>
        }
        componentProps={{ spacing: 5 }}
        loading={state.loading}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
        emptyMessage={t('storePurchaseHistoryEmpty')}
      />
    </BaseLayout>
  )
}

type HeaderTagCellProps = {
  loading: boolean
}
const HeaderTagCell: Cell<HeaderTagCellProps> = ({ loading }) => {
  const { t } = useTranslation('storePurchaseOrders')
  const { routerPages } = useRouter()
  return (
    <CellFrame sx={{ pt: 2, pb: 4 }}>
      {loading ? (
        <SkeletonText variant="h3" width="60%" />
      ) : (
        <Stack direction="row" spacing={2}>
          <Button
            variant="text"
            sx={{ bgcolor: 'grey.50' }}
            href={routerPages.DeliveryManagement}
          >
            {t('checkDeliveryStatus')}
          </Button>
          <Button
            variant="text"
            sx={{ bgcolor: 'grey.50' }}
            href={routerPages.PaymentStatuses}
          >
            {t('ordersAwaitingPayment')}
          </Button>
        </Stack>
      )}
    </CellFrame>
  )
}
